import { useAuth } from '../context/AuthContext';
import { ALL_PERMISSIONS } from '../config/permissions';

/**
 * Hook that resolves the current user's effective permissions.
 *
 * Rules:
 *  - superadmin (is_superuser) → has ALL permissions automatically
 *  - admin with a CustomRole assignment → gets only that role's permissions
 *  - admin with NO CustomRole assignment → gets ALL permissions (backwards-compat)
 *  - member → no admin permissions
 */
export default function usePermissions() {
  const { user } = useAuth();

  if (!user) {
    return { permissions: [], hasPermission: () => false, canAdd: () => false, canEdit: () => false, canDelete: () => false, isSuperAdmin: false };
  }

  const isSuperAdmin = user.is_superuser;

  // Superadmin always has every permission
  if (isSuperAdmin) {
    return {
      permissions: ALL_PERMISSIONS,
      hasPermission: () => true,
      canAdd: () => true,
      canEdit: () => true,
      canDelete: () => true,
      isSuperAdmin: true,
    };
  }

  // Not an admin at all → no admin permissions
  if (user.role !== 'admin') {
    return { permissions: [], hasPermission: () => false, canAdd: () => false, canEdit: () => false, canDelete: () => false, isSuperAdmin: false };
  }

  const perms = user.assigned_permissions || [];

  // If no custom role is assigned, grant all permissions (backwards-compat for existing admins)
  if (!user.has_custom_role) {
    return {
      permissions: ALL_PERMISSIONS,
      hasPermission: () => true,
      canAdd: () => true,
      canEdit: () => true,
      canDelete: () => true,
      isSuperAdmin: false,
    };
  }

  return {
    permissions: perms,
    hasPermission: (...requiredPerms) => requiredPerms.some(p => perms.includes(p)),
    canAdd: (addPerm) => perms.includes(addPerm),
    canEdit: (editPerm) => perms.includes(editPerm),
    canDelete: (deletePerm) => perms.includes(deletePerm),
    isSuperAdmin: false,
  };
}
